"use client";

import { useEffect, useRef, useState } from "react";
import { Dialog } from "@ariakit/react";
import { useRouter } from "next/navigation";
import cn from "classnames";

import type { ReactNode, MouseEvent } from "react";
import type { AdResource } from "../Ad/Ad";
import type { Article } from "../../types/content";

import { Button } from "../Button/Button";
import { Ad } from "../Ad/Ad";
import styles from "./ArticleModal.module.scss";
import { useArticleModalStore } from "../../store/useArticleModalStore";

const brandName =
  process.env.NEXT_PUBLIC_UI_THEME === "ekonomi" ? "ekonomi" : "core";

const ArticleModalControl = ({
  article,
  widescreenAdResource,
  children,
}: {
  article: Article;
  widescreenAdResource: AdResource;
  children: ReactNode;
}) => {
  const router = useRouter();
  const modalRef = useRef<HTMLDivElement>(null);
  const modalInnerRef = useRef<HTMLDivElement>(null);
  const { isModalOpen, modalReferer } = useArticleModalStore();
  const isNativeArticle = article?.meta?.is_sponsored;
  const [shouldClose, setShouldClose] = useState(false);

  useEffect(() => {
    // Changes in modal state has to be placed in history.state
    if (
      history.state?.isModalOpen !== isModalOpen ||
      history.state?.modalReferer !== modalReferer
    ) {
      history.replaceState({ ...history.state, isModalOpen, modalReferer }, "");
    }

    return () => {
      // When closing a modal with an article page as a source the modal should be closed.
      if (shouldClose) {
        setShouldClose(false);
        history.replaceState(
          { ...history.state, isModalOpen: false, modalReferer: undefined },
          "",
        );
      }
    };
  }, [shouldClose, isModalOpen, modalReferer]);

  const closeModal = () => {
    const generalModalIsOpen = document.querySelector("#generalModal");
    if (modalReferer && !generalModalIsOpen) {
      setShouldClose(true);
      router.push(modalReferer, { scroll: false });
    }
  };

  const goBack = () => {
    router.back();
  };

  const onOutsideClick = (event: MouseEvent<HTMLElement>) => {
    if (modalRef.current?.isSameNode(event.target as Node)) {
      closeModal();
    }
  };

  return (
    <Dialog
      portal={false}
      open={isModalOpen}
      onClose={closeModal}
      className={styles.articleModal}
      preventBodyScroll={true}
      backdrop={<div className={styles.backdrop} />}
      getPersistentElements={() => {
        const internalNodeListElements = Array.from(
          document.querySelectorAll("#modal-container"),
        );
        return [
          document.querySelector("#general-modal-container")!,
          ...internalNodeListElements,
        ];
      }}
      focusable={true}
      autoFocus={true}
      initialFocus={modalInnerRef}
    >
      <div
        className={styles.articleModalContent}
        onClick={onOutsideClick}
        ref={modalRef}
      >
        <div className={styles.articleModalContentMain}>
          <section className={styles.inlineArticle}>
            <header className={styles.inlineArticleHeader}>
              <div className={styles.inlineArticleHeaderTools}>
                <Button
                  size="tiny"
                  icon="caretLeft"
                  variant="icon"
                  onClick={goBack}
                  slug="close-article-modal"
                  hiddenText="Gå tillbaka"
                  tabIndex={0}
                />
              </div>
              <div className={styles.inlineArticleHeaderTitle}>
                {brandName === "ekonomi" ? "Omni Ekonomi" : "Omni"}
                {article?.category?.public === false
                  ? ""
                  : ` · ${article?.category?.title}`}
              </div>
              <div
                className={cn([
                  styles.inlineArticleHeaderTools,
                  styles.inlineArticleHeaderToolsRight,
                ])}
              >
                <Button
                  size="tiny"
                  icon="close"
                  variant="icon"
                  onClick={closeModal}
                  slug="close-article-modal"
                  hiddenText="Stäng"
                  tabIndex={0}
                />
              </div>
            </header>
            <div
              className={styles.inlineArticleContent}
              tabIndex={0}
              ref={modalInnerRef}
            >
              {children}
            </div>
          </section>
        </div>
        <div
          className={cn([
            styles.articleModalContentSecondary,
            (isNativeArticle || !widescreenAdResource.havePlacementRule) &&
              styles.hidden,
          ])}
        >
          <Ad resource={widescreenAdResource} />
        </div>
      </div>
    </Dialog>
  );
};

export { ArticleModalControl };
